import { useCallback, useMemo, useState } from 'react';
import { Chart } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { CALL_GROUP_QUEUE_MIN_DATE } from '../../constants';
import { EmptyStateAnalyticsView } from '../empty-state-analytics';
import { useRoutingAnalyticsData } from '../routing-analytics-context';
import { CallVolumeChart } from './chart';
import { GroupHeaderAction } from './chart-action';
import { ChartTableView } from './table-view';
import { type AnalyticsData, AnalyticsDataKey, type ChartView, type GroupPanelContainerProps } from './types';

export const GroupPanelContainer = ({
  filterHintText,
  isLoading,
  data,
  customNameHeaders,
  entityType,
}: GroupPanelContainerProps) => {
  const [view, setView] = useState<ChartView>('dashboard');
  const { t } = useTranslation('analytics-phone');
  const { filters } = useRoutingAnalyticsData();
  const [filteredIds, setFilteredIds] = useState<string[] | null>(null);
  const minDate = new Date('2025-11-01');
  const startDate = filters.start_date ? new Date(filters.start_date) : undefined;
  const isDateValid = startDate ? startDate >= minDate : false;
  const hasNoDataWithValidDate = !data || (data?.length === 0 && isDateValid);

  const onChangeView = useCallback((val: ChartView) => {
    setView(val);
  }, []);

  const onChangeFilter = useCallback((val: string[]) => {
    setFilteredIds(val);
  }, []);

  const categoryBarChartData = useMemo(() => {
    if (!data) return {};
    return data.reduce((acc, sum) => {
      const id = 'callGroupId' in sum ? sum.callGroupId : sum.callQueueId;

      if (!id) return acc;
      if (!filteredIds || (filteredIds.length > 0 && filteredIds.includes(id))) {
        acc[id] = {
          [AnalyticsDataKey.TotalCalls]: sum.totalCalls ?? 0,
          [AnalyticsDataKey.AnsweredCalls]: sum.answeredCalls ?? 0,
          [AnalyticsDataKey.MissedCalls]: sum.missedCalls ?? 0,
          [AnalyticsDataKey.AbandonedCalls]: sum.abandonedCalls ?? 0,
        };
      }

      return acc;
    }, {} as Record<string, Required<AnalyticsData>>);
  }, [data, filteredIds]);

  const chartHeaderTitle = t('Call Volume by {{ entityType }}', { entityType });

  let ChartContent = null;

  if (view === 'dashboard') {
    ChartContent = (
      <CallVolumeChart
        categoryBarChartData={categoryBarChartData}
        filterHintText={filterHintText}
        customNameHeaders={customNameHeaders}
        entityType={entityType}
        title={chartHeaderTitle}
      >
        {(!isDateValid || hasNoDataWithValidDate) && (
          <EmptyStateAnalyticsView
            entityType={entityType}
            minAllowedDate={CALL_GROUP_QUEUE_MIN_DATE}
            isDateValid={isDateValid}
          />
        )}
      </CallVolumeChart>
    );
  } else {
    ChartContent = (
      <ChartTableView
        customNameHeaders={customNameHeaders}
        entityType={entityType}
        categoryBarChartData={categoryBarChartData}
        filterHintText={filterHintText}
        title={chartHeaderTitle}
      >
        {(!isDateValid || hasNoDataWithValidDate) && (
          <EmptyStateAnalyticsView
            entityType={entityType}
            minAllowedDate={CALL_GROUP_QUEUE_MIN_DATE}
            isDateValid={isDateValid}
          />
        )}
      </ChartTableView>
    );
  }

  return (
    <>
      <Chart style={{ marginBottom: 0 }} noBorder isLoading={isLoading}>
        <Chart.Header
          title={t('Call {{ entityType }}', { entityType })}
          headerLevel={2}
          subtitle={
            <GroupHeaderAction
              customNameHeaders={customNameHeaders}
              onChangeFilter={onChangeFilter}
              onChangeView={onChangeView}
              entityType={entityType}
            />
          }
          css={{ padding: theme.spacing(0.5) }}
        />
        {ChartContent}
      </Chart>
    </>
  );
};

export const SubduedText = ({ children, ...rest }: React.ComponentProps<typeof Text>) => (
  <Text color='subdued' {...rest}>
    {children}
  </Text>
);
